import React, { useState, useMemo } from 'react';
import { 
  TextField,
  Autocomplete,
  Box,
  Typography,
  Chip,
  Paper,
  Stack,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountBalance as AccountIcon,
  TrendingUp as TrendingUpIcon,
  Home as HomeIcon,
  Build as BuildIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

const SimpleKontoSelector = ({ 
  buchungskonten = [], 
  selectedKonto, 
  onKontoSelect,
  disabled = false 
}) => {
  const [useSearch, setUseSearch] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Kompatibilitäts-Layer: Unterstützt alte (buchungskonten) und neue (konten) Feldnamen
  const normalizedKonten = useMemo(() => {
    if (!Array.isArray(buchungskonten)) return [];
    
    return buchungskonten.map(konto => ({
      // Neue Felder (konten Tabelle)
      konto_id: konto.konto_id || konto.buchungskontoid,
      kontonummer: konto.kontonummer,
      kontobezeichnung: konto.kontobezeichnung || konto.buchungskonto,
      konto_typ: konto.konto_typ,
      kategorie: konto.kategorie || konto.buchungskontokategorie || 'Unbekannt',
      umsatzsteuersatz: konto.umsatzsteuersatz || konto.umsatzsteuer,
      umlageschluessel: konto.umlageschluessel,
      // Legacy-Felder (für Abwärtskompatibilität)
      buchungskontoid: konto.buchungskontoid || konto.konto_id,
      buchungskonto: konto.buchungskonto || konto.kontobezeichnung,
      buchungskontokategorie: konto.buchungskontokategorie || konto.kategorie || 'Unbekannt',
      umsatzsteuer: konto.umsatzsteuer || konto.umsatzsteuersatz,
      umlagefaehig: konto.umlageschluessel ? true : (konto.umlagefaehig || false)
    }));
  }, [buchungskonten]);

  // Gruppierung nach Kategorien
  const groupedKonten = useMemo(() => {
    return normalizedKonten.reduce((groups, konto) => {
      const kategorie = konto.kategorie;
      if (!groups[kategorie]) groups[kategorie] = [];
      groups[kategorie].push(konto);
      return groups;
    }, {});
  }, [normalizedKonten]);

  // Sortierte Kategorien (größte zuerst)
  const sortedCategories = useMemo(() => {
    return Object.keys(groupedKonten)
      .map(kategorie => ({
        name: kategorie,
        count: groupedKonten[kategorie].length,
        konten: groupedKonten[kategorie].sort((a, b) => {
          const aName = a?.buchungskonto || '';
          const bName = b?.buchungskonto || '';
          return aName.localeCompare(bName);
        })
      }))
      .sort((a, b) => b.count - a.count);
  }, [groupedKonten]);

  // Kategorie-Icons
  const getCategoryIcon = (kategorieName) => {
    const name = kategorieName.toLowerCase();
    if (name.includes('miet') || name.includes('einnahm')) return <TrendingUpIcon sx={{ fontSize: 16 }} />;
    if (name.includes('nebenkosten') || name.includes('betrieb')) return <HomeIcon sx={{ fontSize: 16 }} />;
    if (name.includes('renovier') || name.includes('instand')) return <BuildIcon sx={{ fontSize: 16 }} />;
    if (name.includes('finanz') || name.includes('kredit')) return <MoneyIcon sx={{ fontSize: 16 }} />;
    if (name.includes('verwalt') || name.includes('beratung')) return <BusinessIcon sx={{ fontSize: 16 }} />;
    return <AssignmentIcon sx={{ fontSize: 16 }} />;
  };

  // Kategorie-Farben
  const getCategoryColor = (kategorieName) => {
    const name = kategorieName.toLowerCase();
    if (name.includes('miet') || name.includes('einnahm')) return '#2e7d32';
    if (name.includes('nebenkosten') || name.includes('betrieb')) return '#1976d2';
    if (name.includes('renovier') || name.includes('instand')) return '#7b1fa2';
    if (name.includes('finanz') || name.includes('kredit')) return '#f57c00';
    if (name.includes('verwalt') || name.includes('beratung')) return '#616161';
    return '#424242';
  };

  // Kategorie ausklappen/einklappen
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  // Konto auswählen und alle Kategorien einklappen
  const handleKontoSelect = (konto) => {
    onKontoSelect?.(konto);
    // Alle Kategorien einklappen nach der Auswahl
    setExpandedCategories({});
  };

  if (!Array.isArray(buchungskonten) || buchungskonten.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        Keine Buchungskonten verfügbar
      </Paper>
    );
  }

  return (
    <Box>
      {useSearch ? (
        // Such-Modus
        <Autocomplete
          value={selectedKonto || null}
          onChange={(_, newValue) => {
            onKontoSelect?.(newValue);
            if (newValue) setUseSearch(false);
          }}
          options={buchungskonten}
          getOptionLabel={(option) => option.buchungskonto || ''}
          isOptionEqualToValue={(option, value) => option.buchungskontoid === value?.buchungskontoid}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Konto suchen..."
              placeholder="Tippen Sie um zu suchen..."
              onBlur={() => setTimeout(() => setUseSearch(false), 200)}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <AccountIcon sx={{ fontSize: 16, color: getCategoryColor(option.buchungskontokategorie) }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">{option.buchungskonto}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.buchungskontokategorie}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        />
      ) : (
        // Ausklappbare Kategorien-Liste
        <Box>
          {/* Such-Button */}
          <Paper 
            sx={{ 
              p: 1, 
              mb: 1, 
              cursor: 'pointer', 
              border: '2px dashed #1976d2',
              backgroundColor: '#f3f8ff',
              '&:hover': { backgroundColor: '#e3f2fd' }
            }}
            onClick={() => setUseSearch(true)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <SearchIcon sx={{ color: '#1976d2' }} />
              <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                🔍 Konto suchen...
              </Typography>
            </Box>
          </Paper>

          {/* Ausklappbare Kategorien */}
          <Paper sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0' }}>
            <List dense disablePadding>
              {sortedCategories.map((category) => (
                <React.Fragment key={category.name}>
                  {/* Kategorie-Header (anklickbar) */}
                  <ListItem disablePadding>
                    <ListItemButton 
                      onClick={() => toggleCategory(category.name)}
                      sx={{ 
                        backgroundColor: `${getCategoryColor(category.name)}10`,
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': {
                          backgroundColor: `${getCategoryColor(category.name)}20`,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                        <Box sx={{ color: getCategoryColor(category.name) }}>
                          {getCategoryIcon(category.name)}
                        </Box>
                      </Box>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ 
                            fontWeight: 600, 
                            color: getCategoryColor(category.name) 
                          }}>
                            {category.name} ({category.count})
                          </Typography>
                        }
                      />
                      {expandedCategories[category.name] ? 
                        <ExpandLessIcon sx={{ color: getCategoryColor(category.name) }} /> : 
                        <ExpandMoreIcon sx={{ color: getCategoryColor(category.name) }} />
                      }
                    </ListItemButton>
                  </ListItem>
                  
                  {/* Ausklappbare Konten */}
                  <Collapse in={expandedCategories[category.name]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {category.konten.map((konto) => (
                        <ListItem key={konto.buchungskontoid} disablePadding>
                          <ListItemButton 
                            onClick={() => handleKontoSelect(konto)}
                            disabled={disabled}
                            selected={selectedKonto?.buchungskontoid === konto.buchungskontoid}
                            sx={{ 
                              pl: 4,
                              '&.Mui-selected': {
                                backgroundColor: `${getCategoryColor(category.name)}20`,
                                borderLeft: `3px solid ${getCategoryColor(category.name)}`,
                              },
                              '&:hover': {
                                backgroundColor: `${getCategoryColor(category.name)}08`,
                              }
                            }}
                          >
                            <AccountIcon sx={{ fontSize: 14, color: '#666', mr: 1 }} />
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    {konto.buchungskonto}
                                  </Typography>
                                  {konto.umlagefaehig && <Chip label="U" size="small" sx={{ minWidth: 18, fontSize: '0.5rem' }} />}
                                  {konto.umsatzsteuer && <Chip label={`${konto.umsatzsteuer}%`} size="small" color="warning" sx={{ minWidth: 24, fontSize: '0.5rem' }} />}
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {/* Ausgewähltes Konto */}
      {selectedKonto && (
        <Paper sx={{ mt: 1, p: 1, backgroundColor: '#f3f8ff', border: '1px solid #1976d2' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountIcon sx={{ color: '#1976d2', fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {selectedKonto.buchungskonto}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              • {selectedKonto.buchungskontokategorie}
            </Typography>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default SimpleKontoSelector;
